import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import LightGallery from "lightgallery/react";
import "lightgallery/css/lightgallery.css";
import "lightgallery/css/lg-zoom.css";
import "lightgallery/css/lg-thumbnail.css";
import lgZoom from "lightgallery/plugins/zoom";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../css/DetailRoom.css";
import Swal from "sweetalert2";

import {
  FaCamera,
  FaMapMarkerAlt,
  FaMinus,
  FaPlus,
  FaStar,
  FaStarHalfAlt,
  FaArrowLeft,
} from "react-icons/fa";
import { formatRupiah } from "../utils/formatRupiah";
import { useTranslation } from "react-i18next";
import Rating from "../components/Rating";
import ReviewList from "../components/ReviewList";
import { CompanyContext } from "../context/CompanyContext";

const DetailRoom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { company } = useContext(CompanyContext);

  const [roomType, setRoomType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [roomsCount, setRoomsCount] = useState(1);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [showCheckInCalendar, setShowCheckInCalendar] = useState(false);
  const [showCheckOutCalendar, setShowCheckOutCalendar] = useState(false);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityResponse, setAvailabilityResponse] = useState(null);
  const [availabilityError, setAvailabilityError] = useState(null);
  const lightGalleryRef = useRef(null);

  const [activeTab, setActiveTab] = useState("Overview");

  useEffect(() => {
    const fetchRoomTypeDetail = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_APP_URL}/room-types/${id}/detail`,
          {
            headers: {
              "X-Platform": process.env.REACT_APP_X_PLATFORM,
              "API-Key": process.env.REACT_APP_API_KEY,
              "X-API-Version": process.env.REACT_APP_X_API_VERSION,
              Accept: "application/json",
            },
            withCredentials: true,
          },
        );

        const ok =
          (response.data && response.data.status === "success") ||
          response.data?.success === true;

        if (ok && response.data?.data) {
          const raw = response.data.data;

          const normalized = {
            id: raw.room_type_id ?? raw.id ?? id,
            room_type_code: raw.room_type_code ?? null,
            name: raw.room_type_name ?? raw.name ?? `Room ${id}`,
            description: raw.description ?? raw.short_description ?? "",
            capacity: parseInt(raw.capacity ?? 0, 10),
            occupancy_adult: parseInt(
              raw.occupancy_adult ?? raw.capacity ?? 0,
              10,
            ),
            occupancy_child: parseInt(raw.occupancy_child ?? 0, 10),
            available_rooms: parseInt(
              raw.available_rooms ?? raw.total_rooms ?? 0,
              10,
            ),
            total_rooms: parseInt(raw.total_rooms ?? 0, 10),
            photos: Array.isArray(raw.photos)
              ? raw.photos.map((p) => ({
                  id: p.id,
                  url: p.url,
                  photo_type: p.photo_type || "gallery",
                  display_order: p.display_order ?? 0,
                }))
              : [],
            amenities: Array.isArray(raw.amenities) ? raw.amenities : [],
            facilities: Array.isArray(raw.facilities) ? raw.facilities : [],
            extras: Array.isArray(raw.extras) ? raw.extras : [],
            rate_plans: Array.isArray(raw.rate_plans) ? raw.rate_plans : [],
            rating: raw.rating ?? null,
            price_weekday: raw.price_weekday ?? null,
            price_weekend: raw.price_weekend ?? null,
            price: raw.price ?? null,
          };

          normalized.photos.sort(
            (a, b) => (a.display_order || 0) - (b.display_order || 0),
          );

          setRoomType(normalized);
        } else {
          setError(response.data?.message ?? "Room type not found.");
        }
      } catch (err) {
        console.error("Detail fetch error:", err);
        setError("Failed to load room type details.");
      } finally {
        setLoading(false);
      }
    };

    fetchRoomTypeDetail();
  }, [id]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowGuestDropdown(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="shimmer-card container-shimmer">
          <div className="shimmer-img"></div>
          <div className="p-3">
            <div className="shimmer-title"></div>
            <div className="shimmer-line"></div>
            <div className="shimmer-line"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !roomType) {
    return (
      <div className="container mt-5 text-center text-danger">
        <p>{error ?? "Room not found."}</p>
      </div>
    );
  }

  const {
    name,
    description: roomDescription,
    photos = [],
    facilities = [],
    rate_plans = [],
    rating,
    price_weekday,
    price_weekend,
    price: defaultPrice,
  } = roomType;

  const roomAddress = company ? company.address : "Loading...";

  const priceDetail =
    rate_plans[0]?.price_details && rate_plans[0].price_details.length > 0
      ? rate_plans[0].price_details[0]
      : null;

  const displayPrice = priceDetail ? priceDetail.price : 0;
  const displayDayType = priceDetail ? priceDetail.day_type : "N/A";

  const totalPhotos = photos.length;
  const displayedPhotos = totalPhotos >= 5 ? 5 : totalPhotos;
  const extraPhotos = totalPhotos - displayedPhotos;

  const computedPrice = (() => {
    if (!checkIn) return defaultPrice ?? price_weekday ?? price_weekend ?? 0;
    try {
      const checkInDate = new Date(checkIn);
      const day = checkInDate.getDay();
      if (day === 0 || day === 6)
        return price_weekend ?? defaultPrice ?? price_weekday ?? 0;
      return price_weekday ?? defaultPrice ?? price_weekend ?? 0;
    } catch {
      return defaultPrice ?? price_weekday ?? price_weekend ?? 0;
    }
  })();

  const imagesArr =
    totalPhotos > 0
      ? photos.map((photo, index) => ({
          src: photo.url,
          thumb: photo.url,
          subHtml: `<h4>${name} - Photo ${index + 1}</h4>`,
        }))
      : [];

  const openGallery = (index) => {
    if (
      lightGalleryRef.current &&
      lightGalleryRef.current.instance &&
      typeof lightGalleryRef.current.instance.openGallery === "function"
    ) {
      lightGalleryRef.current.instance.openGallery(index);
    }
  };

  const handleCheckInChange = (date) => {
    setCheckIn(date.toLocaleDateString("en-CA"));
    setShowCheckInCalendar(false);
    if (checkOut && new Date(checkOut) < date) {
      setCheckOut("");
    }
  };

  const handleCheckOutChange = (date) => {
    setCheckOut(date.toLocaleDateString("en-CA"));
    setShowCheckOutCalendar(false);
  };

  const handleAvailabilitySubmit = async (e) => {
    e.preventDefault();
    setAvailabilityLoading(true);
    setAvailabilityResponse(null);
    setAvailabilityError(null);

    const params = {
      check_in_date: checkIn,
      check_out_date: checkOut,
      rooms: [
        {
          room_type_id: roomType.id,
          number_of_rooms: roomsCount,
          guests: adults + children,
        },
      ],
    };

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APP_URL}/availability`,
        {
          params,
          headers: {
            "X-Platform": process.env.REACT_APP_X_PLATFORM,
            "API-Key": process.env.REACT_APP_API_KEY,
            "X-API-Version": process.env.REACT_APP_X_API_VERSION,
            Accept: "application/json",
          },
          withCredentials: true,
        },
      );

      const ok =
        response.data &&
        (response.data.success === true || response.data.status === "success");

      if (
        ok &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        setAvailabilityResponse(response.data.data[0]);

        Swal.fire({
          icon: "success",
          title: "Kamar tersedia!",
          text: "Ketersediaan kamar berhasil ditemukan.",
          timer: 1800,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
          background: "#f0fff4",
          iconColor: "#16a34a",
        });
      } else {
        const msg = response.data?.message || "Tidak ada data ketersediaan.";
        setAvailabilityError(msg);

        Swal.fire({
          icon: "warning",
          title: "Tidak Tersedia",
          text: msg,
          showConfirmButton: true,
          confirmButtonColor: "#f97316",
        });
      }
    } catch (err) {
      console.error("Availability error:", err.response?.data || err);
      const errMsg =
        err.response?.data?.message || "Terjadi kesalahan saat pengecekan.";
      setAvailabilityError(errMsg);

      Swal.fire({
        icon: "error",
        title: "Gagal Mengecek",
        text: errMsg,
        showConfirmButton: true,
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const incrementRooms = () => setRoomsCount((s) => s + 1);
  const decrementRooms = () => setRoomsCount((s) => (s > 1 ? s - 1 : 1));
  const incrementAdults = () => setAdults((s) => s + 1);
  const decrementAdults = () => setAdults((s) => (s > 1 ? s - 1 : 1));
  const incrementChildren = () => setChildren((s) => s + 1);
  const decrementChildren = () => setChildren((s) => (s > 0 ? s - 1 : 0));

  const handleBooking = (ratePlan = null) => {
    if (!checkIn || !checkOut) {
      Swal.fire({
        icon: "warning",
        title: "Pilih tanggal",
        text: "Silakan pilih tanggal check-in dan check-out terlebih dahulu.",
      });
      return;
    }

    const priceFromAvailability =
      availabilityResponse && availabilityResponse.total_price
        ? Number(availabilityResponse.total_price)
        : null;

    const priceFromRatePlan =
      (ratePlan && (ratePlan.total_price_for_stay ?? ratePlan.price)) ??
      (roomType?.rate_plans && roomType.rate_plans[0]
        ? (roomType.rate_plans[0].total_price_for_stay ??
          roomType.rate_plans[0].price)
        : null);

    const bookingPrice =
      priceFromAvailability ??
      priceFromRatePlan ??
      priceDetail?.price ??
      computedPrice ??
      0;

    navigate("/booking", {
      state: {
        roomType,
        computedPrice: bookingPrice,
        adults,
        children,
        roomsCount,
        checkIn,
        checkOut,
        photos: roomType.photos,
      },
    });
  };

  const calcNights = () => {
    if (!checkIn || !checkOut) return 1;
    const inDate = new Date(checkIn);
    const outDate = new Date(checkOut);
    const diffTime = outDate - inDate;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 1;
  };
  const nights = calcNights();

  const roomRating = roomType.rating ? roomType.rating.average : 0;

  const RatingStars = ({ ratingValue }) => {
    const fullStars = Math.floor(ratingValue);
    const halfStar = ratingValue - fullStars >= 0.5;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

    return (
      <div className="d-flex mb-2">
        {Array.from({ length: fullStars }).map((_, i) => (
          <FaStar key={`full-${i}`} className="text-warning" />
        ))}
        {halfStar && <FaStarHalfAlt key="half" className="text-warning" />}
        {Array.from({ length: emptyStars }).map((_, i) => (
          <FaStar key={`empty-${i}`} className="text-muted" />
        ))}
      </div>
    );
  };

  return (
    <div className="detail-room-container" style={{ marginTop: "70px" }}>
      <header className="detail-room-header">
        <div className="header-left">
          <h1 className="property-name">{name}</h1>
          <p className="property-address">
            <FaMapMarkerAlt className="map-icon" /> {roomAddress}
          </p>
        </div>
        <div className="header-right">
          <p className="property-price">
            {formatRupiah(displayPrice)}{" "}
            <span className="per-night">
              {t("perNight")} ({displayDayType})
            </span>
          </p>
        </div>
      </header>

      <div className="photo-gallery">
        <div
          className="main-photo-container"
          onClick={() => openGallery(0)}
          style={{ cursor: "pointer" }}
        >
          {totalPhotos > 0 ? (
            <img src={photos[0].url} alt="Main" className="main-photo" />
          ) : (
            <img
              src="/assets/placeholder.jpg"
              alt="Placeholder"
              className="main-photo"
            />
          )}
          <button className="photo-360-button">
            <FaCamera /> {t("seeAllPhotos")}{" "}
            {extraPhotos > 0 && `+${extraPhotos} Foto`}
          </button>
        </div>

        <div className="thumbnail-row">
          {photos.slice(0, displayedPhotos).map((p, i) => (
            <img
              key={p.id ?? i}
              src={p.url}
              alt={`Thumb ${i + 1}`}
              className="thumbnail"
              onClick={() => openGallery(i)}
              style={{ cursor: "pointer" }}
            />
          ))}
        </div>
      </div>

      <div className="rating-section my-3">
        <RatingStars ratingValue={roomRating} />
        <p className="text-muted small">
          {roomRating
            ? `${roomRating} (${roomType.rating?.total ?? 0} reviews)`
            : "No ratings yet"}
        </p>
      </div>

      {totalPhotos > 0 && (
        <LightGallery
          onInit={(lg) => {
            lightGalleryRef.current = lg;
          }}
          dynamic
          dynamicEl={imagesArr}
          speed={500}
          plugins={[lgZoom, lgThumbnail]}
        />
      )}

      <nav className="detail-nav">
        <ul>
          <li
            className={activeTab === "Overview" ? "active" : ""}
            onClick={() => setActiveTab("Overview")}
          >
            {t("overview")}
          </li>
          <li
            className={activeTab === "Facilities" ? "active" : ""}
            onClick={() => setActiveTab("Facilities")}
          >
            {t("facilities")}
          </li>
          <li
            className={activeTab === "Reviews" ? "active" : ""}
            onClick={() => setActiveTab("Reviews")}
          >
            {t("rating")}
          </li>
        </ul>
      </nav>

      <section className="detail-content">
        {activeTab === "Overview" && (
          <div>
            <p className="section-description">{roomDescription}</p>
          </div>
        )}

        {activeTab === "Facilities" && (
          <div>
            <ul className="facilities-list">
              {facilities && facilities.length > 0 ? (
                facilities.map((fac) => <li key={fac.id}>{fac.name}</li>)
              ) : (
                <li>No facilities available.</li>
              )}
            </ul>
          </div>
        )}

        {activeTab === "Reviews" && (
          <div>
            <ReviewList roomTypeId={roomType.id} />
          </div>
        )}
      </section>

      <Rating roomTypeId={roomType.id} userId={1} />

      <div className="availability-result">
        {availabilityResponse &&
          (availabilityResponse.success === true ||
            availabilityResponse.available_rooms !== undefined) && (
            <div className="availability-table">
              <div className="availability-table-header"></div>
              <div className="availability-table-row">
                {/* 1 */}
                <div className="table-col">
                  <h3>{availabilityResponse.room_type_name}</h3>
                  <ul className="facility-list">
                    {availabilityResponse.facilities?.map((fac) => (
                      <li key={fac.id}>{fac.name}</li>
                    ))}
                  </ul>
                  <a href="#selengkapnya">{t("seeMore")}</a>
                </div>
                {/* 2 */}
                <div className="table-col">
                  {adults} {t("adults")}
                  {children > 0 && `, ${children} ${t("children")}`}
                </div>
                {/* 3 */}
                <div className="table-col">
                  <h4>{formatRupiah(availabilityResponse.total_price)}</h4>
                  <p>{t("includesTaxesAndFees")}</p>
                </div>
                {/* 4 */}
                <div className="table-col">
                  <p>{t("freeCancellationInfo")}</p>
                  <a href="#persyaratan">{t("otherConditions")}</a>
                </div>
                {/* 5 */}
                <div className="table-col">
                  <h4>{formatRupiah(availabilityResponse.total_price)}</h4>
                  <button className="book-now-button" onClick={handleBooking}>
                    {t("bookNow")}
                  </button>
                  <p>{t("redirectNotice")}</p>
                  <ul className="cta-bullets">
                    <li>{t("specialDiscount")}</li>
                    <li>{t("otherTerms")}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

        {availabilityError && (
          <p className="availability-error">{availabilityError}</p>
        )}
      </div>

      <div className="sticky-cta-container">
        <form className="availability-form" onSubmit={handleAvailabilitySubmit}>
          <div className="date-field">
            <label>Check In</label>
            <div
              className="date-input"
              onClick={() => {
                setShowCheckInCalendar(!showCheckInCalendar);
                setShowGuestDropdown(false);
              }}
            >
              {checkIn ? checkIn : "Select Check In"}
            </div>
            {showCheckInCalendar && (
              <Calendar
                minDate={new Date()}
                onChange={handleCheckInChange}
                value={checkIn ? new Date(checkIn) : new Date()}
                className="calendar"
              />
            )}
          </div>

          <div className="date-field">
            <label>Check Out</label>
            <div
              className="date-input"
              onClick={() => {
                setShowCheckOutCalendar(!showCheckOutCalendar);
                setShowGuestDropdown(false);
              }}
            >
              {checkOut ? checkOut : "Select Check Out"}
            </div>
            {showCheckOutCalendar && (
              <Calendar
                minDate={checkIn ? new Date(checkIn) : new Date()}
                onChange={handleCheckOutChange}
                value={checkOut ? new Date(checkOut) : new Date()}
                className="calendar"
              />
            )}
          </div>

          <div className="guest-field" ref={dropdownRef}>
            <label>{t("guest")}</label>
            <div
              className="guest-input"
              onClick={(e) => {
                e.stopPropagation();
                setShowCheckInCalendar(false);
                setShowCheckOutCalendar(false);
                setShowGuestDropdown(!showGuestDropdown);
              }}
            >
              {adults} Dewasa - {children} Anak - {roomsCount} Kamar
            </div>

            {showGuestDropdown && (
              <div className="guest-dropdown">
                <div className="dropdown-item">
                  <span>Kamar</span>
                  <div className="counter">
                    <button
                      type="button"
                      onClick={decrementRooms}
                      className="counter-btn"
                    >
                      <FaMinus />
                    </button>
                    <span>{roomsCount}</span>
                    <button
                      type="button"
                      onClick={incrementRooms}
                      className="counter-btn"
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>

                <div className="dropdown-item">
                  <span>Anak</span>
                  <div className="counter">
                    <button
                      type="button"
                      onClick={decrementChildren}
                      className="counter-btn"
                    >
                      <FaMinus />
                    </button>
                    <span>{children}</span>
                    <button
                      type="button"
                      onClick={incrementChildren}
                      className="counter-btn"
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>

                <div className="dropdown-item">
                  <span>Dewasa</span>
                  <div className="counter">
                    <button
                      type="button"
                      onClick={decrementAdults}
                      className="counter-btn"
                    >
                      <FaMinus />
                    </button>
                    <span>{adults}</span>
                    <button
                      type="button"
                      onClick={incrementAdults}
                      className="counter-btn"
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <button type="submit" className="sticky-cta-button">
            {availabilityLoading ? "Loading..." : t("checkAvailability")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DetailRoom;
